import classNames from 'classnames'
import { ReactElement, useId } from 'react'
import { AISparkle } from 'app/components/Icon/AIBadgeIcon'

type Props = {
  className?: string
}

/**
 * Milestone divider rendered immediately after the last AI agent message in a
 * thread when the thread's AI Agent has been manually turned off — visually
 * marking the moment the agent stopped responding. Human messages that
 * follow the toggle stay below the divider.
 *
 * Visual companion to {@link AgentEngagedDivider} — same horizontal-rule +
 * centered pill shape, but muted (greyscaled sparkle + neutral border / text).
 *
 * NOTE: anchored to "last agent message" as a heuristic. Without a backend
 * toggle-event log on the Thread, that's the best proxy we have for when the
 * agent was turned off.
 */
export function AgentDisabledDivider({ className }: Props): ReactElement {
  const gradientId = `agent-disabled-sparkle-${useId()}`

  return (
    <div data-testid="agent-disabled-divider" className={classNames(`flex items-center`, className)}>
      <div className="h-px w-full bg-surface-strong" />
      <div className="flex shrink-0 items-center gap-1 rounded border-[0.5px] border-strong bg-surface-muted px-1.5 py-0.5">
        <span className="inline-flex opacity-60 grayscale">
          <AISparkle className="h-4 w-4 shrink-0" gradientId={gradientId} />
        </span>
        <span className="whitespace-nowrap text-center text-label-xs font-medium text-subtle">Agent Disabled</span>
      </div>
      <div className="h-px w-full bg-surface-strong" />
    </div>
  )
}
